import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom, useAtomValue } from "jotai";
import { Card, Collapse } from "reactstrap";

import SidebarHeader from "./SidebarHeader";
import SidebarSwitcher from "./SidebarSwitcher";
import SharedSearch from "../PageView/PageViewTable/SharedToolbar/SharedSearch";
import useGlobalApps from "../../api/api-hooks/globalApps/useGlobalApps";
import useGetCloudApps from "../../api/api-hooks/cloudApps/useGetCloudApps";
import SkeletonLoader from "../../elements/SkeletonLoader";
import FieldUtils from "../../utils/FieldUtils";
import { atomWithStorage } from "jotai/utils";
import { tenantIDAtom } from "../../atoms/GlobalAtoms";
import CustomLinks2 from "./CustomLinks/CustomLinks2";

interface Props {
  open: boolean;
  smallScreen: boolean;
  switcherOpen: boolean;
  requiresWorkspace: boolean;
  workspaceLoading: boolean;
  workspace: any;
  workspaceID: string;
  workspaceIds: string[];
  toggleOpen: () => void;
  links: { id: string; type: string; css: string; pageType: string }[];
  setOpen: (val: boolean) => void;
  children: React.ReactNode;
}

const selectedAppMapAtom = atomWithStorage<Record<string, any>>(
  "selectedApps",
  {}
);

const MobileSidebar = ({
  open,
  smallScreen,
  workspace,
  workspaceID,
  toggleOpen,
  children,
  setOpen,
}: Props) => {
  const navigate = useNavigate();
  const { globalApps, loadingApps } = useGlobalApps();
  const tenantID = useAtomValue(tenantIDAtom);
  const { cloudApps, loadingApps: loadingCloudApps } = useGetCloudApps();
  const [appsOpen, setAppsOpen] = useState(false);
  const [selectedAppMap, setSelectedAppMap] = useAtom(selectedAppMapAtom);
  const [searchValue, setSearchValue] = useState("");

  const selectedApp = selectedAppMap?.[workspaceID || ""];
  const setSelectedApp = (app: any) => {
    workspaceID && setSelectedAppMap((map) => ({ ...map, [workspaceID]: app }));
  };

  // To reset the selected app in case the app visibility was changed.
  useEffect(() => {
    setSelectedAppMap((map) => ({ ...map, [workspaceID]: null }));
  }, [workspace?.AppsVisibility]);

  const allApps = useMemo(() => {
    if (
      !globalApps ||
      !cloudApps ||
      (!globalApps?.length && !cloudApps?.length) ||
      loadingApps ||
      loadingCloudApps
    )
      return [];

    let filteredGlobalApps: any[] | undefined;
    let filteredCloudApps: any[] | undefined;
    const appsVisibility = workspace?.AppsVisibility ?? {};

    const installedGlobalApps = workspace?.Sidebar?.flatMap((item: any) => {
      return !!item?.GlobalAppID && appsVisibility[item?.GlobalAppID] !== false
        ? [item]
        : [];
    });

    if (searchValue) {
      const search = searchValue.toLowerCase();

      filteredGlobalApps = installedGlobalApps?.filter((globalApp: any) => {
        return globalApp.Name.toLowerCase().includes(search);
      });
      filteredCloudApps = cloudApps?.filter((app) => {
        return !app.GlobalAppID && app.Title?.toLowerCase?.()?.includes(search);
      });
    } else {
      filteredCloudApps = cloudApps?.filter((app) => !app.GlobalAppID);
      filteredGlobalApps = installedGlobalApps;
    }

    return [...(filteredGlobalApps || []), ...(filteredCloudApps || [])];
  }, [
    globalApps,
    cloudApps,
    searchValue,
    workspace?.Sidebar,
    workspace?.AppsVisibility,
  ]);

  const selectApp = (app: any) => {
    let finalApp = selectedApp && selectedApp?.id === app.id ? undefined : app;

    setSelectedApp(finalApp);
    setAppsOpen(false);

    if (finalApp?.DefaultPage) {
      navigate(
        `/clouds/${tenantID}/workspaces/${workspaceID}/pages/${finalApp.DefaultPage}/views/None`
      );

      // To close the sidebar when there is a default page selected.
      setOpen(false);
    }
  };

  // The links to be displayed in the Sidebar. When an App is selected, only it's page are displayed.
  const sidebarLinks = selectedApp
    ? selectedApp.Items
    : workspace?.Sidebar?.filter(
        (link: any) => link.Type === "Group" || link.Type === "PageLink"
      );

  return (
    <>
      <aside className={`${open ? "sidebar open overlay" : "sidebar"}`}>
        <SidebarHeader toggleOpen={toggleOpen} open={open} />
        <SidebarSwitcher
          open={open}
          icon={!selectedApp ? "mdi mdi-apps" : undefined}
          rightIcon="mdi mdi-chevron-down"
          css="tw:mx-3 tw:hover:bg-sidebar-link tw:border-lighter tw:text-medium"
          text={
            selectedApp?.id
              ? selectedApp.CloudAppID
                ? selectedApp.Name
                : selectedApp.Title
              : "Apps"
          }
          onClick={() => setAppsOpen(!appsOpen)}
          image={selectedApp?.Icon ? selectedApp?.Icon : undefined}
        />
        <Collapse isOpen={appsOpen}>
          <Card
            className="mx-2 px-2 shadow-md pb-3"
            style={{
              boxShadow: "0 4px 8px 0 rgba(0,0,0,0.2)",
              borderRadius: "5px",
            }}
          >
            {/* @ts-ignore */}
            <SharedSearch
              isMobile={true}
              setSimpleSearchValue={(val) => setSearchValue(val)}
              searchTime={0}
            />
            {loadingApps || loadingCloudApps ? (
              <>
                <SkeletonLoader height={100} width="100" css="my-0" />
              </>
            ) : (
              <div
                className="d-flex align-items-start flex-wrap p-2 mt-2"
                style={{
                  rowGap: "10px",
                  justifyContent: "space-evenly",
                  maxHeight: 300,
                  overflowY: "auto",
                }}
              >
                {!allApps?.length && (
                  <div className="text-sm bg-superlight w-100 text-center rounded py-2 text-dark">
                    No Apps Were Found
                  </div>
                )}
                {allApps.map((app) => {
                  const title = app?.Title || app?.Name;
                  const isSelected = app.id === selectedApp?.id;

                  return (
                    <div
                      key={app?.id}
                      className="d-flex flex-column justify-content-start align-items-center cursor-pointer text-center "
                      style={{
                        width: "100px",
                        borderRadius: "10px",
                      }}
                      onClick={() => selectApp(app)}
                    >
                      <div
                        style={{
                          padding: 10,
                          borderRadius: "10px",
                          backgroundColor: isSelected ? "#c8f3d8" : undefined,
                        }}
                      >
                        {app?.Icon ? (
                          <img
                            src={app?.Icon}
                            alt="App Icon"
                            style={{
                              width: 42,
                              height: 42,
                              borderRadius: "10px",
                            }}
                          />
                        ) : (
                          <div
                            className="text-white bg-primary d-flex align-items-center justify-content-center"
                            style={{
                              borderRadius: "10px",
                              color: "white",
                              fontSize: 20,
                              width: 42,
                              height: 42,
                            }}
                          >
                            {FieldUtils.getInitials({
                              _RecordName: title || "App",
                            })}
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          width: 100,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                        className={`m-0 p-0 mt-1 text-xs font-weight-500 ${
                          isSelected ? "text-primary" : "text-dark"
                        }`}
                      >
                        {title}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </Collapse>
        {sidebarLinks && (
          <CustomLinks2
            key={sidebarLinks.id}
            sidebarItems={sidebarLinks}
            appsVisibility={workspace?.AppsVisibility}
            reorderEnabled={false}
          />
        )}
      </aside>
      <main className={open && !smallScreen ? "content ml-open" : "content"}>
        {children}
      </main>
    </>
  );
};

export default MobileSidebar;
